<template>
  <div class="medicine-integration-modern">
    <!-- No Medicines State -->
    <div v-if="medicines.length == 0" class="no-medicines">
      <div class="no-medicines-content">
        <i class="fas fa-pills no-medicines-icon"></i>
        <h3 class="no-medicines-title">Prescrição sem medicamentos</h3>
        <p class="no-medicines-text">
          Esta prescrição não possui medicamentos ativos para integração
        </p>
      </div>
    </div>

    <!-- Medicines List -->
    <div v-else class="medicines-container">
      <!-- Header -->
      <div class="medicines-header">
        <h2 class="medicines-title">
          <i class="fas fa-pills mr-2"></i>
          Medicamentos da Prescrição
        </h2>
        <p class="medicines-subtitle">
          Selecione os medicamentos para enviar à Box
        </p>
      </div>

      <!-- Medicines Grid -->
      <div class="medicines-grid">
        <div
          v-for="(item, key_medicine) in medicines"
          :key="key_medicine"
          class="medicine-card"
          :class="{
            'medicine-selected': selectedBoxMedicineIds.includes(item.id),
            'medicine-no-box': item.box_medicines.length == 0,
            'medicine-available': item.box_medicines.length > 0,
            'medicine-in-box': item.exists_box_api_data,
          }"
        >
          <!-- Medicine Info -->
          <div class="medicine-info">
            <!-- Main Info -->
            <div class="medicine-main">
              <div class="medicine-header">
                <h3 class="medicine-name">{{ item.medicine_name }}</h3>
                <div class="medicine-badges">
                  <span
                    v-if="
                      item.type_of_recipe_name == 'CONTROLE ESPECIAL - 2 VIAS'
                    "
                    class="badge badge-controlled"
                  >
                    Controle Especial
                  </span>
                  <span v-else class="badge badge-normal">
                    {{ item.type_of_recipe_name }}
                  </span>
                  <span class="badge badge-frequency">
                    {{ item.medicine_amount_of_times_name }}
                  </span>
                </div>
              </div>

              <div class="medicine-ingredient">
                <span class="ingredient-label">Princípio Ativo:</span>
                <span class="ingredient-value">{{
                  item.medicine_active_ingredient
                }}</span>
              </div>

              <div class="alert v-dark m-0" v-if="item.exists_box_api_data">
                <div class="mb-2">
                  Medicamento <strong>{{ item.medicine_name }}</strong> já foi
                  enciado para a <strong>BOX</strong>
                </div>
                <div class="text-right">
                  <ConfirmationDialog
                    v-if="permission('write', 'box')"
                    headerClass="modal-header"
                    message-title="Cancelar medicamento da BOX"
                    :message-body="`Deseja realmente cancelar o medicamento ${item.medicine_name} da Box?`"
                    open-text="Cancelar medicamento da BOX"
                    open-class="btn-modern btn-modern-delete"
                    open-left-icon="fa-trash"
                    @yes="removeMedicineFromBox(item)"
                    :loading="loading"
                    :disabled="loading"
                  />
                </div>
              </div>
            </div>

            <!-- Medicine Details Grid -->
            <div class="medicine-details">
              <!-- Administration Details -->
              <div class="detail-section">
                <h4 class="detail-title">
                  <i class="fas fa-route mr-2"></i>
                  Administração
                </h4>

                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Via:</span>
                    <span class="detail-value">{{
                      item.route_of_administration_name
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">Frequência:</span>
                    <span class="detail-value">{{
                      handleFrequency(item.frequency)
                    }}</span>
                  </div>

                  <div class="detail-item" v-if="item.frequency != 'EVENTUAL'">
                    <span class="detail-label">Repetição:</span>
                    <span class="detail-value">{{
                      handleEvery(item.every, item.frequency)
                    }}</span>
                  </div>

                  <div
                    class="detail-item"
                    v-if="item.medicine_amount_of_times_name"
                  >
                    <span class="detail-label">Quantidade diária:</span>
                    <span class="detail-value">{{
                      item.medicine_amount_of_times_name
                    }}</span>
                  </div>
                </div>

                <!-- Week Days (for weekly frequency) -->
                <div v-if="item.frequency == 'SEMANAL'" class="week-days">
                  <span class="detail-label">Dias da semana:</span>
                  <div class="days-container">
                    <span v-if="item.dom" class="day-badge">DOM</span>
                    <span v-if="item.seg" class="day-badge">SEG</span>
                    <span v-if="item.ter" class="day-badge">TER</span>
                    <span v-if="item.qua" class="day-badge">QUA</span>
                    <span v-if="item.qui" class="day-badge">QUI</span>
                    <span v-if="item.sex" class="day-badge">SEX</span>
                    <span v-if="item.sab" class="day-badge">SAB</span>
                  </div>
                </div>
              </div>

              <!-- Dosage Details -->
              <div class="detail-section">
                <h4 class="detail-title">
                  <i class="fas fa-syringe mr-2"></i>
                  Dosagem
                </h4>

                <!-- Shifts Dosage -->
                <div
                  v-if="item.medicine_amount_of_times_type == 'SHIFTS'"
                  class="shifts-dosage"
                >
                  <div class="shift-grid">
                    <div
                      v-if="item.dawn && item.dawn_quantity"
                      class="shift-item dawn"
                    >
                      <div class="shift-label">
                        <i class="fas fa-moon"></i>
                        Madrugada
                      </div>
                      <div class="shift-value">
                        {{
                          item.drops_dawn_quantity
                            ? `${item.drops_dawn_quantity} gota(s)`
                            : item.ui_dawn_quantity
                            ? `${item.ui_dawn_quantity} UI`
                            : `${item.dawn_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.morning && item.morning_quantity"
                      class="shift-item morning"
                    >
                      <div class="shift-label">
                        <i class="fas fa-sun"></i>
                        Manhã
                      </div>
                      <div class="shift-value">
                        {{
                          item.drops_morning_quantity
                            ? `${item.drops_morning_quantity} gota(s)`
                            : item.ui_morning_quantity
                            ? `${item.ui_morning_quantity} UI`
                            : `${item.morning_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.afternoon && item.afternoon_quantity"
                      class="shift-item afternoon"
                    >
                      <div class="shift-label">
                        <i class="fas fa-cloud-sun"></i>
                        Tarde
                      </div>
                      <div class="shift-value">
                        {{
                          item.drops_afternoon_quantity
                            ? `${item.drops_afternoon_quantity} gota(s)`
                            : item.ui_afternoon_quantity
                            ? `${item.ui_afternoon_quantity} UI`
                            : `${item.afternoon_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.night && item.night_quantity"
                      class="shift-item night"
                    >
                      <div class="shift-label">
                        <i class="fas fa-moon"></i>
                        Noite
                      </div>
                      <div class="shift-value">
                        {{
                          item.drops_night_quantity
                            ? `${item.drops_night_quantity} gota(s)`
                            : item.ui_night_quantity
                            ? `${item.ui_night_quantity} UI`
                            : `${item.night_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <!-- Additional shifts -->
                    <div
                      v-if="item.shift_5 && item.shift_5_quantity"
                      class="shift-item additional"
                    >
                      <div class="shift-label">5º Turno</div>
                      <div class="shift-value">
                        {{
                          item.drops_shift_5_quantity
                            ? `${item.drops_shift_5_quantity} gota(s)`
                            : item.ui_shift_5_quantity
                            ? `${item.ui_shift_5_quantity} UI`
                            : `${item.shift_5_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.shift_6 && item.shift_6_quantity"
                      class="shift-item additional"
                    >
                      <div class="shift-label">6º Turno</div>
                      <div class="shift-value">
                        {{
                          item.drops_shift_6_quantity
                            ? `${item.drops_shift_6_quantity} gota(s)`
                            : item.ui_shift_6_quantity
                            ? `${item.ui_shift_6_quantity} UI`
                            : `${item.shift_6_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.shift_7 && item.shift_7_quantity"
                      class="shift-item additional"
                    >
                      <div class="shift-label">7º Turno</div>
                      <div class="shift-value">
                        {{
                          item.drops_shift_7_quantity
                            ? `${item.drops_shift_7_quantity} gota(s)`
                            : item.ui_shift_7_quantity
                            ? `${item.ui_shift_7_quantity} UI`
                            : `${item.shift_7_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>

                    <div
                      v-if="item.shift_8 && item.shift_8_quantity"
                      class="shift-item additional"
                    >
                      <div class="shift-label">8º Turno</div>
                      <div class="shift-value">
                        {{
                          item.drops_shift_8_quantity
                            ? `${item.drops_shift_8_quantity} gota(s)`
                            : item.ui_shift_8_quantity
                            ? `${item.ui_shift_8_quantity} UI`
                            : `${item.shift_8_quantity} ${item.unit_of_measurement_abbreviation}(s)`
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Single Dosage -->
                <div
                  v-if="
                    item.medicine_amount_of_times_type == 'DAY' ||
                    item.frequency == 'EVENTUAL'
                  "
                  class="single-dosage"
                >
                  <div class="dosage-item">
                    <span class="dosage-label">Quantidade:</span>
                    <span class="dosage-value">
                      {{
                        item.drops_quantity
                          ? `${item.drops_quantity} gota(s)`
                          : item.ui_quantity
                          ? `${item.ui_quantity} UI`
                          : `${item.quantity} ${item.unit_of_measurement_name}(s)`
                      }}
                    </span>
                  </div>
                </div>

                <!-- Special Instructions -->
                <div v-if="item.instruction" class="instruction-item">
                  <span class="instruction-label">
                    <i class="fas fa-info-circle mr-1"></i>
                    Instrução:
                  </span>
                  <span class="instruction-value">{{ item.instruction }}</span>
                </div>
              </div>

              <!-- Duration and Schedule -->
              <div class="detail-section" v-if="item.frequency != 'EVENTUAL'">
                <h4 class="detail-title">
                  <i class="fas fa-calendar-alt mr-2"></i>
                  Duração e Horários
                </h4>

                <!-- Duration Details -->
                <div class="duration-details">
                  <div
                    v-if="
                      item.frequency != 'EVENTUAL' &&
                      (item.duration == 'ATE_A_DATA' ||
                        item.duration == 'DURANTE_UM_PERIODO')
                    "
                    class="detail-item"
                  >
                    <span class="detail-label">Data de início:</span>
                    <span class="detail-value">{{
                      formatDate(item.start_date, false)
                    }}</span>
                  </div>

                  <div v-if="item.frequency != 'EVENTUAL'" class="detail-item">
                    <span class="detail-label">Duração:</span>
                    <span class="detail-value">{{
                      handleDuration(item.duration)
                    }}</span>
                  </div>

                  <div
                    v-if="
                      item.frequency != 'EVENTUAL' &&
                      item.duration == 'DURANTE_UM_PERIODO'
                    "
                    class="detail-item"
                  >
                    <span class="detail-label">Período:</span>
                    <span class="detail-value"
                      >{{ item.duration_time }} dia(s)</span
                    >
                  </div>

                  <div
                    v-if="
                      item.frequency != 'EVENTUAL' &&
                      item.duration == 'ATE_A_DATA'
                    "
                    class="detail-item"
                  >
                    <span class="detail-label">Data de término:</span>
                    <span class="detail-value">{{
                      formatDate(item.end_date, false)
                    }}</span>
                  </div>
                </div>

                <!-- Schedule Times -->
                <div v-if="item.frequency != 'EVENTUAL'" class="schedule-times">
                  <h5 class="schedule-title">Horários de Administração</h5>
                  <div class="times-grid">
                    <div
                      v-for="prescription_medicine_time in item.prescription_medicine_time"
                      :key="prescription_medicine_time.id"
                      class="time-item"
                    >
                      <div class="time-hour">
                        <i class="fas fa-clock mr-1"></i>
                        {{
                          prescription_medicine_time.time
                            ? String(prescription_medicine_time.time).substr(
                                0,
                                5
                              )
                            : "Não informado"
                        }}
                      </div>
                      <div class="time-dosage">
                        {{
                          prescription_medicine_time.drops
                            ? `${prescription_medicine_time.drops} gota(s)`
                            : prescription_medicine_time.ui
                            ? `${prescription_medicine_time.ui} UI`
                            : `${prescription_medicine_time.quantity} ${prescription_medicine_time.unit_of_measurement_value}(s)`
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Box Selection -->
          <div class="box-selection">
            <!-- No Box Available -->
            <div v-if="item.box_medicines.length == 0" class="no-box-warning">
              <div class="warning-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="warning-content">
                <h4 class="warning-title">Fabricante não encontrado</h4>
                <p class="warning-text">
                  Este medicamento não possui fabricante disponível na Box
                </p>
              </div>
            </div>

            <!-- Box Selection Available -->
            <div v-if="item.box_medicines.length > 0" class="box-selector">
              <div class="selector-header">
                <h4 class="selector-title">
                  <i class="fas fa-cube mr-2"></i>
                  Selecionar Fabricante na Box
                </h4>
              </div>

              <v-select
                label=""
                outlined
                dense
                item-value="id"
                v-model="item.boxifarma_medicamento_id"
                :items="item.box_medicines"
                class="modern-box-select"
                :disabled="item.exists_box_api_data"
              >
                <template v-slot:item="{ item: boxItem }">
                  <div class="box-option">
                    <div class="box-medicine-name">
                      {{ boxItem.medicamento }}
                    </div>
                    <div class="box-details">
                      <div class="box-detail">
                        <span class="box-detail-label">Fabricante:</span>
                        <span class="box-detail-value">{{
                          boxItem.fabricante
                        }}</span>
                      </div>
                      <div class="box-detail">
                        <span class="box-detail-label">Tipo:</span>
                        <span class="box-detail-value">{{
                          boxItem.tipo_medicamento
                        }}</span>
                      </div>
                      <div class="box-detail">
                        <span class="box-detail-label">Forma:</span>
                        <span class="box-detail-value">{{
                          boxItem.forma
                        }}</span>
                      </div>
                      <div class="box-detail">
                        <span class="box-detail-label">Receita:</span>
                        <span class="box-detail-value">{{
                          boxItem.tipo_receita
                        }}</span>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-slot:selection="{ item: boxItem }">
                  <div class="box-selected">
                    <div class="selected-name">{{ boxItem.medicamento }}</div>
                    <div class="selected-manufacturer">
                      {{ boxItem.fabricante }}
                    </div>
                  </div>
                </template>
              </v-select>

              <!-- Selection Required Warning -->
              <div
                v-if="
                  !item.boxifarma_medicamento_id &&
                  selectedBoxMedicineIds.includes(item.id)
                "
                class="selection-warning"
              >
                <i class="fas fa-exclamation-circle mr-2"></i>
                Selecione o fabricante do medicamento na Box
              </div>

              <div
                class="text-center"
                v-if="
                  item.box_medicines.length > 0 &&
                  !item.exists_box_api_data &&
                  item.boxifarma_medicamento_id
                "
              >
                <ConfirmationDialog
                  v-if="permission('write', 'box')"
                  headerClass="modal-header"
                  message-title="Finalizar Integração"
                  :message-body="`Deseja realmente enviar os medicamentos selecionados para a Box?`"
                  open-text="Enviar medicamento para Box"
                  open-class="btn-modern btn-modern-save"
                  open-left-icon="fa-paper-plane"
                  @yes="createMedicineFromBox(item)"
                  :loading="loading"
                  :disabled="loading"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Messages -->
      <div v-if="errors.length > 0" class="errors-section">
        <ErrorsMessages :errors="errors" />
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../../Components/ErrorsMessages";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import { formatDate } from "../../../lib/format";
import permission from "../../../lib/permission";
import toast from "../../../lib/ToastNotification";
import api from "../../../services/api";

export default {
  name: "BoxMedicine",
  components: {
    ErrorsMessages,
    ConfirmationDialog,
  },
  props: {
    box_id: [String, Number],
    resident_id: [String, Number],
    group_prescription_medicine_id: [String, Number],
    medicines: Array,
  },
  data() {
    return {
      selectedBoxMedicineIds: [],
      errors: [],
      loading: false,
    };
  },
  watch: {
    medicines: {
      handler() {
        let { medicines } = this;
        let selectedBoxMedicineIds = [];
        for (let medicine of medicines) {
          if (
            this.selectedBoxMedicineIds.includes(medicine.id) ||
            medicine.boxifarma_medicamento_id
          ) {
            selectedBoxMedicineIds.push(medicine.id);
          }
        }
        this.selectedBoxMedicineIds = selectedBoxMedicineIds;
      },
    },
  },
  created() {},
  methods: {
    formatDate,
    permission,

    handleFrequency(frequency) {
      let frequencys = {
        DIARIO: "DIÁRIO",
        SEMANAL: "SEMANAL",
        MENSAL: "MENSAL",
        ANUAL: "ANUAL",
        EVENTUAL: "EVENTUAL",
        default: "NÃO ENCONTRADO",
      };
      return frequencys[frequency] || frequencys.default;
    },

    handleEvery(every, frequency) {
      let frequencys = {
        DIARIO: "DIA",
        SEMANAL: "SEMANA",
        MENSAL: "MÊS",
        ANUAL: "ANO",
      };
      return `A CADA ${every} ${frequencys[frequency]}`;
    },

    handleDuration(duration) {
      let durations = {
        CONTINUO: "CONTÍNUO",
        DURANTE_UM_PERIODO: "DURANTE UM PERÍODO",
        ATE_A_DATA: "ATÉ A DATA",
        default: "EVENTUAL",
      };
      return durations[duration] || durations.default;
    },

    async save() {
      try {
        this.loading = true;
        const {
          box_id,
          resident_id,
          group_prescription_medicine_id,
          medicines,
          selectedBoxMedicineIds,
        } = this;

        let selectedMedicines = [];
        for (let medicine of medicines) {
          if (selectedBoxMedicineIds.includes(medicine.id)) {
            selectedMedicines.push({
              prescription_medicine_id: medicine.id,
              medicine_id: medicine.medicine_id,
              boxifarma_medicamento_id: medicine.boxifarma_medicamento_id
                ? medicine.boxifarma_medicamento_id
                : null,
            });
          }
        }

        let params = {
          box_id,
          resident_id,
          group_prescription_medicine_id,
          medicines: selectedMedicines,
        };

        const response = await api.post(
          "/rest-home/box/create/prescription",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          this.errors = [];
          toast(message, "success");
          this.$emit("success");
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async createMedicineFromBox(item) {
      try {
        this.loading = true;

        const { box_id, resident_id, group_prescription_medicine_id } = this;

        let { id, medicine_id, boxifarma_medicamento_id } = item;

        let params = {
          box_id,
          resident_id,
          group_prescription_medicine_id,
          medicines: [
            {
              prescription_medicine_id: id,
              medicine_id,
              boxifarma_medicamento_id,
            },
          ],
        };

        const response = await api.post(
          "/rest-home/box/create/prescription",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          this.errors = [];
          toast(message, "success");
          this.$emit("success");
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async removeMedicineFromBox(item) {
      try {
        this.loading = true;

        const { box_id, resident_id } = this;

        const { id } = item;

        let params = { id, box_id, resident_id };

        const response = await api.put(
          "/rest-home/box/remove/prescription",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          this.errors = [];
          toast(message, "success");
          this.$emit("success");
          this.selectedBoxMedicineIds = [];
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.medicine-integration-modern {
  padding: 0;
}

/* No Medicines State */
.no-medicines {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

.no-medicines-content {
  text-align: center;
  padding: 80px 24px;
}

.no-medicines-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-medicines-title {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.no-medicines-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

/* Medicines Container */
.medicines-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.medicines-header {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
}

.medicines-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.medicines-subtitle {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

/* Medicines Grid */
.medicines-grid {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.medicine-card {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  background: #f8fafc;
  transition: all 0.3s ease;
  overflow: hidden;
}

.medicine-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.medicine-selected {
  border-color: #059669;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.2);
}

.medicine-in-box {
  border-color: #059669;
  background: linear-gradient(135deg, #e3f7f0 0%, #85d1b9 100%);
}

.medicine-no-box {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.medicine-available {
  border-color: #e5e7eb;
}

.medicine-available:hover {
  border-color: #cbd5e1;
}

/* Medicine Selector */
.medicine-checkbox {
  display: none;
}

.checkbox-label {
  display: block;
  width: 24px;
  height: 24px;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.checkbox-label::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 6px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.medicine-checkbox:checked + .checkbox-label {
  background: #059669;
  border-color: #059669;
}

.medicine-checkbox:checked + .checkbox-label::after {
  opacity: 1;
}

/* Medicine Info */
.medicine-info {
  padding: 24px;
}

.medicine-main {
  padding-bottom: 20px;
}

.medicine-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.medicine-name {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  padding-right: 40px;
}

.medicine-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-controlled {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.badge-normal {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.badge-frequency {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.medicine-ingredient {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ingredient-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ingredient-value {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  word-break: break-word;
}

/* Medicine Details */
.medicine-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.detail-title i {
  color: #0f65b0;
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.detail-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  text-align: right;
}

/* Week Days */
.week-days {
  margin-top: 12px;
}

.days-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.day-badge {
  padding: 4px 8px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  border: 1px solid #818cf8;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Shifts Dosage */
.shifts-dosage {
  margin-top: 12px;
}

.shift-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.shift-item {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.shift-item.dawn {
  border-color: #6366f1;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
}

.shift-item.morning {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.shift-item.afternoon {
  border-color: #06b6d4;
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
}

.shift-item.night {
  border-color: #8b5cf6;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
}

.shift-item.additional {
  border-color: #64748b;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.shift-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.shift-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

/* Single Dosage */
.single-dosage {
  margin-top: 12px;
}

.dosage-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.dosage-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dosage-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

/* Instruction */
.instruction-item {
  margin-top: 16px;
  padding: 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #f59e0b;
  border-radius: 8px;
}

.instruction-label {
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  display: block;
  margin-bottom: 4px;
}

.instruction-value {
  font-size: 14px;
  font-weight: 500;
  color: #92400e;
  word-break: break-word;
}

/* Duration and Schedule */
.duration-details {
  margin-bottom: 20px;
}

.schedule-times {
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}

.schedule-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
}

.times-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.time-item {
  padding: 8px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
}

.time-hour {
  font-size: 12px;
  font-weight: 600;
  color: #0f65b0;
  margin-bottom: 4px;
}

.time-dosage {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

/* Box Selection */
.box-selection {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin: 20px 24px 24px 24px;
}

/* No Box Warning */
.no-box-warning {
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
}

.warning-icon {
  width: 48px;
  height: 48px;
  background: #f59e0b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 4px 0;
}

.warning-text {
  font-size: 14px;
  color: #92400e;
  margin: 0;
}

/* Box Selector */
.box-selector {
  padding: 12px;
}

.selector-header {
  margin-bottom: 6px;
}

.selector-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.selector-title i {
  color: #0f65b0;
}

/* Box Option */
.box-option {
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  width: 100%;
}

.box-option:last-child {
  border-bottom: none;
}

.box-medicine-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.box-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.box-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.box-detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.box-detail-value {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

/* Box Selected */
.box-selected {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selected-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.selected-manufacturer {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

/* Selection Warning */
.selection-warning {
  margin-top: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 1px solid #f87171;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  display: flex;
  align-items: center;
}

/* Error Section */
.errors-section {
  padding: 0 24px 24px 24px;
}

/* Save Section */
.save-section {
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.btn-save-modern {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 16px !important;
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3) !important;
  transition: all 0.3s ease !important;
  min-width: 200px !important;
}

.btn-save-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4) !important;
}

.btn-save-modern:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.save-hint {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .medicine-details {
    grid-template-columns: 1fr;
  }

  .shift-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .times-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }
}

@media (max-width: 768px) {
  .medicines-container {
    border-radius: 12px;
  }

  .medicines-header {
    padding: 20px 16px;
  }

  .medicines-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .medicines-grid {
    padding: 16px;
    gap: 16px;
  }

  .medicine-info {
    padding: 20px;
  }

  .medicine-header {
    gap: 8px;
  }

  .medicine-name {
    font-size: 18px;
    padding-right: 0;
  }

  .medicine-badges {
    gap: 6px;
  }

  .detail-section {
    padding: 16px;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-value {
    text-align: left;
  }

  .box-details {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .box-selection,
  .box-selector {
    margin: 16px;
    padding: 16px;
  }

  .no-box-warning {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .save-section {
    padding: 20px 16px;
  }
}

@media (max-width: 480px) {
  .medicines-header {
    padding: 16px;
  }

  .medicines-title {
    font-size: 18px;
  }

  .medicines-subtitle {
    font-size: 14px;
  }

  .medicine-name {
    font-size: 16px;
  }

  .medicine-badges {
    flex-direction: column;
    align-items: flex-start;
  }

  .shift-grid {
    grid-template-columns: 1fr;
  }

  .times-grid {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  }

  .box-option {
    padding: 8px 0;
  }

  .box-medicine-name {
    font-size: 14px;
  }

  .btn-save-modern {
    width: 100% !important;
    padding: 14px 24px !important;
    font-size: 14px !important;
  }
}

/* Vuetify Overrides */
.modern-box-select >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-box-select >>> .v-text-field__slot input {
  font-weight: 500 !important;
}

.modern-box-select >>> .v-select__selection {
  max-width: 100% !important;
}

/* Custom Scrollbar */
.medicine-details::-webkit-scrollbar {
  width: 6px;
}

.medicine-details::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.medicine-details::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.medicine-details::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.medicine-card {
  animation: slideIn 0.3s ease forwards;
}

.medicine-card:nth-child(1) {
  animation-delay: 0.1s;
}
.medicine-card:nth-child(2) {
  animation-delay: 0.2s;
}
.medicine-card:nth-child(3) {
  animation-delay: 0.3s;
}
.medicine-card:nth-child(4) {
  animation-delay: 0.4s;
}
.medicine-card:nth-child(5) {
  animation-delay: 0.5s;
}
</style>